import { createElement as rc, memo, useCallback, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import useEventSink from '../../../hooks/useEventSink';
import { webOnlyProperties } from 'lib_ui-services';
import { ExternalLink, styled, View, webOnlyStyles } from 'lib_ui-primitives';

const stopClickPropagation = event => event.stopPropagation?.();

// Keep URL truncation and icon positioning in the browser; React Native uses ExternalLink's native layout.
let ListExternalLink = styled(ExternalLink)``;
ListExternalLink = webOnlyStyles(ListExternalLink)`
    align-self: center;
    box-sizing: border-box;
    display: block;
    margin-bottom: 0;
    min-width: 0;
    overflow: hidden;
    /* Reserve space so truncated URL text does not overlap the external-link icon. */
    padding-right: 20px;
    position: relative;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;

    & > .material-icons {
        margin-left: 0;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
    }

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
`;
ListExternalLink.displayName = 'ListExternalLink';

function LinkColumn(props) {
    const {
        record,
        Column,
        width,
        style,
        testAttrs,
        hNode: { id, propertyName, propertyUrl, openInNewWindow = true }
    } = props || { hNode: {} };

    const [, publish] = useEventSink();

    const onAttachmentClick = useCallback(
        event => {
            event.stopPropagation?.();
            publish(record, { verb: 'view', namespace: 'file', relation: 'attachment', type: 'content' });
        },
        [publish, record]
    );

    const { title, url, isExternalUrl, cursor, textDecoration, icon } = useMemo(() => {
        const title = record[propertyName];
        const url = record[propertyUrl];
        const isExternalUrl = typeof url === 'string' && /^https?:\/\/\S+$/i.test(url);
        const cursor = url ? 'pointer' : 'default';
        const textDecoration = url ? 'underline' : 'none';
        const icon = record.type?.startsWith('image') ? '🖼️' : '📄';
        return { title, url, isExternalUrl, cursor, textDecoration, icon };
    }, [propertyName, propertyUrl, record]);

    const linkWidth = width ? Number(width) - 22 : width;

    return rc(
        Fragment,
        null,
        rc(
            Column,
            {
                style: {
                    paddingLeft: '0',
                    paddingRight: '0'
                }
            },
            icon
        ),
        isExternalUrl
            ? rc(
                  Column,
                  {
                      id,
                      width: linkWidth,
                      style: {
                          ...style,
                          paddingLeft: '3px'
                      },
                      ...testAttrs,
                      as: View
                  },
                  rc(ListExternalLink, {
                      url,
                      title,
                      openInNewWindow,
                      includeExternalIcon: openInNewWindow,
                      onClick: stopClickPropagation,
                      style: { width: '100%' }
                  })
              )
            : rc(
                  Column,
                  {
                      id,
                      onClick: url ? onAttachmentClick : undefined,
                      width: linkWidth,
                      style: {
                          ...style,
                          paddingLeft: '3px',
                          textDecoration,
                          ...webOnlyProperties({ cursor })
                      },
                      ...testAttrs
                  },
                  title
              )
    );
}

LinkColumn.propTypes = {
    record: PropTypes.object,
    Column: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
    hNode: PropTypes.object
};

export default memo(LinkColumn);
